import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, message, Typography, Checkbox, Select } from 'antd';
import CodeVerification from '../../../Common/components/CodeVerification';
import Spinner from 'react-bootstrap/Spinner';
import './IframeLoginModal.css';
import { getIframesToken, PATH_CUSTOMER_ID } from '../../constants';
import Terms from '../../../signup/terms';
import { useDispatch } from 'react-redux';
import styles from '../../../Management/ApplicationSetting/index.module.css';
import DividerWithText from '../../../Common/components/DividerLineWithText';
import jwt_decode from 'jwt-decode';
import { useTrackEvent } from '../../analytics/useTrackEvent';

const { Text } = Typography;

const IframeLoginModal = ({ 
    isOpen, 
    onClose, 
    onSuccess, 
    iframeConfig,
    isSignUp, 
    setIsSignUp, 
    companyName, 
    setShowSelfPayUpgradeModal, 
    companyPayMode, 
    setTriggerType, 
    SCENARIO_AI_USE,
    setApplyPromoCode,
    currentBillingStatus,
    promoCodeValidity,
    profile,
    usage
}) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const { trackSignUp, trackSignIn } = useTrackEvent();
    const roleValue = Form.useWatch('role', form);
    const [signupFormValues, setSignupFormValues] = useState({});
    const [currentStep, setCurrentStep] = useState('email'); // 'email', 'verification', 'registration', 'agreement'
    const [loading, setLoading] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [showResult, setShowResult] = useState(false);
    const [verified, setVerified] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [userExists, setUserExists] = useState(false);
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const [googleReady, setGoogleReady] = useState(false);

    // Handle Google OAuth callback
    const handleGoogleCallback = useCallback(async (response) => {
        setLoading(true);
        try {
            const userObject = jwt_decode(response.credential);
            const email = userObject.email?.toLowerCase().trim();
            const firstName = userObject.given_name || '';
            const lastName = userObject.family_name || '';

            if (!email) {
                message.error('Unable to get email from Google account. Please try again.');
                setLoading(false);
                return;
            }

            setUserEmail(email);

            // Call Google sign-in endpoint
            const signInResponse = await fetch("/los/googlesignin", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-GOOGLETOKEN": response.credential,
                    "X-IFRAMECID": PATH_CUSTOMER_ID,
                },
                body: JSON.stringify(userObject),
            });

            if (signInResponse.status !== 200) {
                const errorText = await signInResponse.text();
                message.error('Unable to sign in with Google. Please try again.');
                setLoading(false);
                return;
            }

            const result = await signInResponse.json();

            if (result.Status !== "OK") {
                // User not found - handle sign-up flow
                if (isSignUp) {
                    // Pre-fill registration form with Google data
                    form.setFieldsValue({
                        firstName: firstName,
                        lastName: lastName,
                    });
                    setCurrentStep('registration');
                    setIsSignUp(true);
                    message.info('Please complete your registration to continue.');
                } else {
                    message.error('Account not found. Please sign up first.');
                }
                setLoading(false);
                return;
            }

            // User exists - sign them in
            if (result.Claims && result.Claims.length > 0) {
                // Find the claim matching the current customer ID
                let token = null;
                if (result.Claims.length === 1) {
                    token = result.Claims[0].Claim;
                } else {
                    // Multiple companies - find matching one
                    const matchingClaim = result.Claims.find(claim => {
                        try {
                            const payload = jwt_decode(claim.Claim);
                            return payload.customerid === PATH_CUSTOMER_ID;
                        } catch (e) {
                            return false;
                        }
                    });
                    if (matchingClaim) {
                        token = matchingClaim.Claim;
                    } else {
                        // Use first claim if no match found
                        token = result.Claims[0].Claim;
                    }
                }

                if (token) {
                    localStorage.setItem(getIframesToken(), token);
                    message.success({
                        content: 'Signed in successfully!',
                        duration: 1,
                        className: styles.successMessage,
                    });
                    window.dispatchEvent(new CustomEvent('ZeitroIframeSessionUpdated', { 
                        detail: {
                            token: token,
                        }
                    }));

                    let signInProfile = {}
                    
                    // Track sign-in event
                    try {
                        const decodedToken = jwt_decode(token);
                        signInProfile = {
                            AccountTier: decodedToken.accounttier,
                            LoID: decodedToken.loid,
                            Email: decodedToken.email,
                            Role: decodedToken.role,
                            created_at: decodedToken.iat ? new Date(decodedToken.iat * 1000).toISOString() : undefined,
                        };
                        trackSignIn(signInProfile, usage, currentBillingStatus);
                    } catch (e) {
                        console.error('Error tracking sign-in:', e);
                        trackSignIn(profile, usage, currentBillingStatus);
                    }
                    
                    onClose();

                    // Handle promo code and upgrade modal
                    if (companyPayMode !== 'self_pay' || profile?.AccountTier == 'paid' || signInProfile?.AccountTier == 'paid') {
                        return;
                    }
                    if (PATH_CUSTOMER_ID === 'successlending' && currentBillingStatus === 'inactive' && promoCodeValidity === 'valid') {
                        setTriggerType('promo-code-applied');
                        setApplyPromoCode(true);
                        setShowSelfPayUpgradeModal(true);
                    } else {
                        setTriggerType('signin');
                        setApplyPromoCode(false);
                        if (companyPayMode === 'self_pay') {
                            setTimeout(() => {
                                setShowSelfPayUpgradeModal(true);
                            }, 1000);
                        }
                    }
                } else {
                    message.error('Unable to authenticate. Please try again.');
                }
            } else {
                // No claims - user not found, go to registration
                if (isSignUp) {
                    form.setFieldsValue({
                        firstName: firstName,
                        lastName: lastName,
                    });
                    setCurrentStep('registration');
                    message.info('Please complete your registration to continue.');
                } else {
                    message.error('Account not found. Please sign up first.');
                }
            }
        } catch (error) {
            console.error('Google sign-in error:', error);
            message.error('An error occurred during Google sign-in. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [isSignUp, form, PATH_CUSTOMER_ID, getIframesToken, onClose, profile, currentBillingStatus, promoCodeValidity, companyPayMode, setTriggerType, setApplyPromoCode, setShowSelfPayUpgradeModal, trackSignIn]);

    // Render hidden Google button
    const renderGoogleButton = useCallback(() => {
        try {
            const hiddenContainer = document.getElementById("hiddenGoogleSignin");
            if (hiddenContainer && window.google && window.google.accounts && window.google.accounts.id) {
                // Clear any existing button
                hiddenContainer.innerHTML = '';
                window.google.accounts.id.renderButton(hiddenContainer, {
                    width: '0',
                    height: '0',
                    text: 'signin_with',
                    size: 'large',
                    shape: 'pill',
                    logo_alignment: 'center',
                    theme: 'outline',
                    type: 'standard'
                });
            }
        } catch (error) {
            console.error('Error rendering Google button:', error);
        }
    }, []);

    // Handle Google sign-in button click
    const handleGoogleSignIn = () => {
        const hiddenButton = document.getElementById("hiddenGoogleSignin");
        if (hiddenButton) {
            const googleButton = hiddenButton.querySelector('div[role="button"]');
            if (googleButton) {
                googleButton.click();
            } else {
                // Fallback to prompt method
                if (window.google && window.google.accounts && window.google.accounts.id) {
                    window.google.accounts.id.prompt();
                }
            }
        } else {
            // Fallback to prompt method
            if (window.google && window.google.accounts && window.google.accounts.id) {
                window.google.accounts.id.prompt();
            }
        }
    };

    // Initialize Google OAuth
    useEffect(() => {
        if (!isOpen || currentStep !== 'email') return;

        const initializeGoogle = () => {
            if (typeof window !== 'undefined' && window.google && window.google.accounts && window.google.accounts.id) {
                try {
                    window.google.accounts.id.initialize({
                        client_id: "265683149831-9a27eaupjqjt6vh5vuaa54bfld627fre.apps.googleusercontent.com",
                        callback: handleGoogleCallback,
                    });
                    renderGoogleButton();
                    setGoogleReady(true);
                } catch (error) {
                    console.error('Error initializing Google OAuth:', error);
                }
            } else {
                // Retry after a short delay if Google script hasn't loaded yet
                setTimeout(initializeGoogle, 100);
            }
        };

        initializeGoogle();
    }, [isOpen, currentStep, handleGoogleCallback, renderGoogleButton]);

    // First step: Email verification
    const handleEmailSubmit = async (values) => {
        setLoading(true);
        setUserEmail(values.email);
        
        try {
            setLoading(true);

            // Send verification code
            const response = await fetch('/los/generateonetimepasscode', {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache': 'no-cache'
                },
                body: JSON.stringify({
                    email: values.email,
                    customerID: PATH_CUSTOMER_ID,
                }),
            });

            if (response.status !== 200) {
                message.error('Failed to send verification code. Please try again.');
                setLoading(false);
                return;
            } else {
                setCurrentStep('verification');
                message.success({
                    content: 'The verification code has been sent to your email',
                    duration: 1,
                    className: styles.successMessage,
                });
            }
        } catch (error) {
            console.log(error)
            message.error('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Registration form submission (for new users)
    const handleRegistrationSubmit = async (values) => {
        // setCurrentStep('agreement');
        // setSignupFormValues(values);
        
        try {
            const response = await fetch('/los/iframe/signuploaccount', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userEmail.toLowerCase().trim(),
                    firstName: values.firstName,
                    lastName: values.lastName,
                    nmls: values.nmls,
                    customerID: PATH_CUSTOMER_ID,
                    branchName: values.organizationName,
                    licenseID: values.brokerageNmls,
                    roles: [values.role],
                }),
            });

            if (response.ok) {
                const result = await response.json();
                localStorage.setItem(getIframesToken(), result.Token);
                message.success({
                    content: 'Account created successfully!',
                    duration: 1,
                    className: styles.successMessage,
                });                
                window.dispatchEvent(new CustomEvent('ZeitroIframeSessionUpdated', { 
                    detail: {
                        token: result.Token,
                    }
                }));
                
                // Track sign-up event
                try {
                    const decodedToken = jwt_decode(result.Token);
                    const signUpProfile = {
                        AccountTier: decodedToken.accounttier || 'free',
                        LoID: decodedToken.loid,
                        Email: decodedToken.email,
                        Role: values.role,
                        created_at: new Date().toISOString(),
                    };
                    trackSignUp(signUpProfile, usage, currentBillingStatus);
                } catch (e) {
                    console.error('Error tracking sign-up:', e);
                }
                
                onClose();
                
                // Check promo code validity directly after sign up with the new token
                if (companyPayMode === 'self_pay') {
                    try {
                        const promoCodeResponse = await fetch("/api/v1/los/stripe/checkPromoCodeValidity", {
                            method: 'POST',
                            headers: {
                                'Authorization': 'Bearer ' + result.Token,
                            },
                            body: JSON.stringify({
                                promoCodeId: ''    //leave blank will check for default self pay 1 month free promo code
                            }),
                        });

                        if (promoCodeResponse.ok) {
                            const promoCodeData = await promoCodeResponse.json();
                            if (PATH_CUSTOMER_ID === 'successlending' && promoCodeData.status === 'valid') {
                                setTriggerType('promo-code-applied');
                                setApplyPromoCode(true);
                            } else {
                                setTriggerType('signup');
                                setApplyPromoCode(false);
                            }
                        } else {
                            setTriggerType('signup');
                        }
                    } catch (error) {
                        console.error("Error checking promo code validity:", error);
                        setTriggerType('signup');
                    }
                    
                    setTimeout(() => {
                        if (companyPayMode === 'self_pay') {
                            setShowSelfPayUpgradeModal(true);
                        }
                    }, 1000);
                }   
            } else {
                const errorText = await response.text();
                message.error('Failed to sign up your account. ' + errorText);
            }
        } catch (error) {
            message.error('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCodeVerification = async (code) => {
        if (code.length !== 4) return;
        
        setVerifyingCode(true);
        try {
            const response = await fetch('/los/iframe/verifycodeandsignin', {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Cache': 'no-cache',
                },
                body: JSON.stringify({
                    email: userEmail,
                    code: code,
                    customerID: PATH_CUSTOMER_ID,
                }),
            });

            if (response.ok) {
                const result = await response.json();
                localStorage.setItem(getIframesToken(), result.Token);
                message.success({
                    content: 'Sign in successfully!',
                    duration: 1,
                    className: styles.successMessage,
                });
                window.dispatchEvent(new CustomEvent('ZeitroIframeSessionUpdated', { 
                    detail: {
                        token: result.Token,
                    }
                }));
                
                // Track sign-in event
                let decodedToken;
                let newAccountTier;
                try {
                    decodedToken = jwt_decode(result.Token);
                    newAccountTier = decodedToken.accounttier;
                    const signInProfile = {
                        AccountTier: newAccountTier,
                        LoID: decodedToken.loid,
                        Email: decodedToken.email,
                        Role: decodedToken.role,
                        created_at: decodedToken.iat ? new Date(decodedToken.iat * 1000).toISOString() : undefined,
                    };
                    trackSignIn(signInProfile, usage, currentBillingStatus);
                } catch (e) {
                    console.error('Error tracking sign-in:', e);
                }
                onClose();
                // 登录后刷新页面，为了渲染刷新后的tags数据
                // setTimeout(() => {
                //     window.location.reload();
                // }, 500);
                
                // Show upgrade modal for self-pay companies (similar to sign-up flow)
                if (companyPayMode === 'self_pay') {
                    if (newAccountTier !== 'paid' && PATH_CUSTOMER_ID === 'successlending' && currentBillingStatus === 'inactive' && promoCodeValidity === 'valid') {
                        setTriggerType('promo-code-applied');
                        setApplyPromoCode(true);
                        setShowSelfPayUpgradeModal(true);
                    } else {
                        if (newAccountTier !== 'paid') {
                            setTriggerType('signin');
                            setApplyPromoCode(false);
                            setShowSelfPayUpgradeModal(true);
                        }
                    }
                }
            } else {
                const errorText = await response.text();
                if (errorText.includes('Account not found')) {
                    setCurrentStep('registration');
                    return;
                } else {
                    message.error('Invalid or expired code. Please try again.');
                    setShowResult(true);
                    setVerified(false);
                }
            }
        } catch (error) {
            console.log(error)
            message.error('An error occurred. Please try again.');
        }
        finally {
            setVerificationCode('');
            setVerifyingCode(false);
        }
    };

    const handleResendCode = async () => {
        setSendingCode(true);
        
        try {
            const response = await fetch('/los/generateonetimepasscode', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    Email: userEmail,
                    customerID: PATH_CUSTOMER_ID,
                }),
            });

            if (response.ok) {
                message.success({
                    content: 'The verification code has been resent to your email',
                    duration: 1,
                    className: styles.successMessage,
                });
            } else {
                message.error('Failed to resend code. Please try again.');
            }
        } catch (error) {
            console.error('Error resending code:', error);
            message.error('An error occurred. Please try again.');
        } finally {
            setSendingCode(false);
        }
    };

    // Handle agreement acceptance and account creation
    const handleAgreementAccept = async () => {

        setLoading(true);
        
        try {
            const response = await fetch('/los/iframe/signuploaccount', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userEmail.toLowerCase().trim(),
                    firstName: signupFormValues.firstName,
                    lastName: signupFormValues.lastName,
                    nmls: signupFormValues.nmls,
                    customerID: PATH_CUSTOMER_ID,
                    branchName: signupFormValues.organizationName,
                    licenseID: signupFormValues.brokerageNmls,
                    roles: [signupFormValues.role],
                }),
            });

            if (response.ok) {
                const result = await response.json();
                localStorage.setItem(getIframesToken(), result.Token);
                message.success({
                    content: 'Your account has been created successfully!',
                    duration: 1,
                    className: styles.successMessage,
                });
                window.dispatchEvent(new CustomEvent('ZeitroIframeSessionUpdated', { 
                    detail: {
                        token: result.Token,
                    }
                }));
                onClose();
                // 登录后刷新页面，为了渲染刷新后的tags数据
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } else {
                const errorText = await response.text();
                message.error('Failed to sign up your account. ' + errorText);
            }
        } catch (error) {
            message.error('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset form and state
        form.resetFields();
        setCurrentStep('email');
        setVerificationCode('');
        setShowResult(false);
        setVerified(false);
        setUserEmail('');
        setUserExists(false);
        setAgreementAccepted(false);
        onClose && onClose();
    };

    const handleBackToEmail = () => {
        setCurrentStep('email');
        setShowResult(false);
        setVerified(false);
        setVerificationCode('');
        setUserExists(false);
    };

    const handleBackToRegistration = () => {
        setCurrentStep('registration');
        setShowResult(false);
        setVerified(false);
        setVerificationCode('');
    };

    const email = iframeConfig?.email || 'support@zeitro.com';

    // Email step - first step in the flow
    const renderEmailStep = () => (
        <div className="iframe-login-modal-right-panel-content">
            <div className="main-iframe-login-content">
                <div className="iframe-login-modal-header email-step-margin">
                    <div className="signin-link">
                        <Text>{isSignUp ? 'Already have an account? ' : 'Don\'t have an account? '}</Text>
                        <Button type="link" className="signin-link-button" onClick={() => setIsSignUp(!isSignUp)}>{isSignUp?'Sign in':'Sign up'}</Button>
                    </div>
                </div>

                <div className="iframe-login-modal-title">
                    {iframeConfig ? <img src={iframeConfig?.logo} style={{height : 40, marginBottom:28}} /> : null}
                    <p style={{marginTop: iframeConfig ? 10 : undefined}}>
                        {isSignUp ? 'Create your account' : 'Sign in to your account'}
                    </p>
                </div>

                <div id="hiddenGoogleSignin" style={{ display: 'none' }}></div>
                <div className="google-signin-container" style={{
                    width: '100%',
                    maxWidth: window.innerWidth <= 768 ? '320px' : '400px',
                    margin: '0 auto 20px',
                }}>
                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        className="google-signin-button"
                        disabled={loading || !googleReady}
                        style={{
                            width: '100%',
                            height: '40px',
                            backgroundColor: '#fff',
                            border: '1px solid #dadce0',
                            color: '#222',
                            borderRadius: '24px',
                            fontSize: '18px',
                            lineHeight: '22px',
                            fontFamily: 'Inter',
                            fontWeight: '400',
                            cursor: loading || !googleReady ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            opacity: loading || !googleReady ? 0.6 : 1,
                        }}
                        onMouseOver={(e) => {
                            if (!loading && googleReady) {
                                e.currentTarget.style.backgroundColor = '#f7f8ff';
                                e.currentTarget.style.border = '1px solid #d8eaff';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!loading && googleReady) {
                                e.currentTarget.style.backgroundColor = '#fff';
                                e.currentTarget.style.border = '1px solid #dadce0';
                            }
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                        >
                            <path
                                d="M21.56 11.25c0-.78-.07-1.53-.2-2.25H11v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                            />
                            <path
                                d="M11 22c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H1.17v2.84C2.99 19.53 6.7 22 11 22z"
                                fill="#34A853"
                            />
                            <path
                                d="M4.84 13.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V6.07H1.17C.42 7.3 0 8.6 0 10s.42 2.7 1.17 3.93l2.67-2.84.01-.01z"
                                fill="#FBBC05"
                            />
                            <path
                                d="M11 4.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C16.45 1.19 13.97 0 11 0 6.7 0 2.99 2.47 1.17 6.07l3.67 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                            />
                        </svg>
                        <span style={{ width: '177px', textAlign: 'left'}}>{isSignUp ? 'Sign up with Google' : 'Sign in with Google'}</span>
                    </button>
                </div>

                <DividerWithText text="OR" />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleEmailSubmit}
                    requiredMark={false}
                    className="email-form"
                >
                    <Form.Item
                        name="email"
                        label="Email address"
                        rules={[
                            { required: true, message: 'Email is required' },
                            { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                    >
                        <Input 
                            placeholder="Enter your email address"
                            size="large"
                            type="email"
                        />
                    </Form.Item>

                    <div className="form-actions">
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            size="large"
                            loading={loading}
                            className="continue-button"
                            disabled={loading}
                        >
                            Continue
                        </Button>
                    </div>
                </Form>
            </div>

            <div className="copyright">
                <Text>© 2026 Powered by Zeitro. </Text>
                <div className='copyright-line' />
                <a href={`mailto:${email}`}>Need help</a>
            </div>
        </div>
    );

    // Registration step for new users
    const renderRegistrationStep = () => (
        <div className="iframe-login-modal-right-panel-content">
            <div className="main-iframe-login-content">
                <div className="iframe-login-modal-header registration-step-margin">
                    <h2 className="iframe-login-modal-title registration-step-title-margin">You are joining {companyName}</h2>
                    <p className="iframe-login-modal-subtitle">Please complete the fields below to finish signing up and continue.</p>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleRegistrationSubmit}
                    requiredMark={true}
                    className="registration-form"
                >
                    <div className="form-row">
                        <Form.Item
                            name="firstName"
                            label="First name"
                            rules={[
                                { required: true, message: 'First name is required' },
                            ]}
                            className="form-item-half"
                        >
                            <Input 
                                placeholder="Enter legal first name"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            name="lastName"
                            label="Last name"
                            rules={[
                                { required: true, message: 'Last name is required' },
                            ]}
                            className="form-item-half"
                        >
                            <Input 
                                placeholder="Enter legal last name"
                                size="large"
                            />
                        </Form.Item>
                    </div>
     
                    <div className="form-row">
                        <Form.Item
                            name="role"
                            label="Role"
                            rules={[
                                { required: true, message: 'Please select a role' },
                            ]}
                            className="form-item-half"
                        >
                            <Select 
                                rootClassName="role-selection-select"
                                placeholder="Select your role"
                                size="large"
                                // onChange={(value) => setRole(value)}
                                // value={role}
                            >
                                <Select.Option value="LoanOfficer">Loan officer</Select.Option>
                                <Select.Option value="LoanProcessor">Loan processor</Select.Option>
                                <Select.Option value="TrainingStaff">Training staff</Select.Option>
                                <Select.Option value="Underwriter">Underwriter</Select.Option>
                                <Select.Option value="AccountExecutive">Account executive</Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="nmls"
                            label="NMLS ID"
                            rules={[
                                { required: roleValue === 'LoanOfficer', message: 'NMLS ID is required' },
                            ]}
                            className="form-item-half"
                        >
                            <Input 
                                placeholder="Enter NMLS ID"
                                size="large"
                            />
                        </Form.Item>
                    </div>

                    <div className="form-row">
                        {
                            PATH_CUSTOMER_ID !== 'greenbox' && <Form.Item
                                name="organizationName"
                                label="Organization Name"
                                rules={[
                                    { required: true, message: 'Organization name is required' },
                                ]}
                                className="form-item-half"
                            >
                                <Input 
                                    placeholder="Enter the company/brokerage name"
                                    size="large"
                                />
                            </Form.Item>
                        }

                        <Form.Item
                            name="brokerageNmls"
                            label="Brokerage NMLS ID"
                            className="form-item-half"
                        >
                            <Input 
                                placeholder="Enter your company's NMLS ID"
                                size="large"
                            />
                        </Form.Item>
                    </div>

                    <div className="form-row checkbox-margin">
                        <Checkbox
                            checked={agreementAccepted}
                            onChange={(e) => setAgreementAccepted(e.target.checked)}
                        >
                            I confirm that I have read, consent and agree to Zeitro's <span className="service-agreement-link" onClick={() => window.open('/services/terms', '_blank')}>Software as a Service Agreement</span>.
                        </Checkbox>
                    </div>

                    <div className="form-actions">
                        <div
                            onClick={handleBackToEmail}
                            className="back-link-btn"
                        >
                            Back
                        </div>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={loading}
                            className={!agreementAccepted ? "sign-up-submit-btn-disabled" : "sign-up-submit-btn"}
                            disabled={!agreementAccepted}
                        >
                            Get started
                        </Button>
                    </div>
                </Form>
            </div>

            <div className="copyright">
                <Text>© 2026 Zeitro. All rights reserved.</Text>
            </div>
        </div>
    );

    // Verification step
    const renderVerificationStep = () => (
        <div className="iframe-login-modal-right-panel-content">
            <div className="main-iframe-login-content">
                <div className="iframe-login-modal-header">
                    <div className="verification-icon">
                        <img src="/images/checkEmail2.png" className="envelope-icon" alt="Envelope icon" />
                    </div>
                    <h2 className="iframe-login-modal-title">Check your email</h2>
                    <div className="email-info">
                        <Text>
                            We sent a code to <strong>{userEmail}</strong>
                        </Text>
                        <Button 
                            type="link" 
                            onClick={handleBackToEmail}
                            className="edit-email-link"
                        >
                            Edit
                        </Button>
                    </div>
                    <p className="verification-subtitle">
                        Enter the code to activate your account
                    </p>
                </div>

                <div className="verification-content">
                    <div className="code-input-container">
                        {(verifyingCode || sendingCode) ? (
                            <Spinner animation="border" size="lg" />
                        ) : (
                            <CodeVerification 
                                value={verificationCode} 
                                onChange={(code) => {
                                    setVerificationCode(code);
                                    handleCodeVerification(code);
                                }} 
                            />
                        )}
                    </div>

                    <div className="resend-section">
                        <Text>Didn't receive the email? </Text>
                        <Button 
                            type="link" 
                            onClick={handleResendCode}
                            loading={sendingCode}
                            className="resend-link"
                        >
                            Resend
                        </Button>
                    </div>
                </div>
            </div>

            <div className="copyright">
                <Text>© 2026 Zeitro. All rights reserved.</Text>
            </div>
        </div>
    );

    // Agreement step
    const renderAgreementStep = () => (
        <div className="iframe-login-modal-right-panel-content agreement-step-padding">
            <div className="iframe-login-modal-header">
                <h2 className="iframe-login-modal-title agreement-step-title-align">Software as a service agreement</h2>
            </div>

            <div className="agreement-content">
                <div className="agreement-text">
                   <Terms />
                </div>

                <div className="agreement-checkbox">
                    <Checkbox 
                        checked={agreementAccepted}
                        onChange={(e) => setAgreementAccepted(e.target.checked)}
                    >
                        I have read and understood the Software as a Service Agreement.
                    </Checkbox>
                </div>

                <div className="form-actions">
                    <div className="back-link-btn" onClick={handleBackToRegistration}>
                        Back
                    </div>
                    <Button 
                        type="primary" 
                        onClick={handleAgreementAccept}
                        size="large"
                        loading={loading}
                        disabled={!agreementAccepted}
                        className={!agreementAccepted ? "sign-up-submit-btn-disabled" : "sign-up-submit-btn"}
                    >
                        Accept
                    </Button>
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 'email':
                return renderEmailStep();
            case 'verification':
                return renderVerificationStep();
            case 'registration':
                return renderRegistrationStep();
            default:
                return renderEmailStep();
        }
    };

    const displayImage = () => {
        return (
            <div className='iframe-login-img-box'>
                <div className='iframe-login-logo-box'>
                    <img src='/images/login-logo.png' />
                </div>
                <div className='iframe-login-bg-box'>
                    <img src='/images/login-img.png' />
                </div>
                <div className='iframe-login-img-box-content'>
                    <div className='iframe-login-img-box-content-title'>
                    “After years in real estate, I’ve never seen a tool as efficient as Zeitro. It’s like having a personal assistant who handles all the tedious tasks, freeing me to focus on my clients.”
                    </div>
                    <div className='iframe-login-img-box-content-name'>
                    Bob A, Broker owner
                    </div>
                    <div className='iframe-login-img-box-content-address'>
                    Serenity Home Loans
                    </div>
                </div>
            </div>
        )
    }
    
   const ICON_BANER = iframeConfig?.modalbaner;

   const width = currentStep === 'agreement' ? 590 : (1040 - (iframeConfig?.modalbaner ? 0 : 520));

    return (
        <>
            <Modal
                open={isOpen}
                onCancel={handleClose}
                footer={null}
                width={width}
                height={currentStep === 'agreement' ? 624 : undefined}
                centered
                className="iframe-login-modal"
                maskClosable={false}
            >
                <div className="iframe-login-modal-container">
                    {currentStep !== 'agreement' && 
                        <>
                            {ICON_BANER ? <img src={ICON_BANER} className="iframe-login-modal-left-img-panel" /> : null}
                           {/* {iframeConfig ? null : <div className="iframe-login-modal-left-panel">
                                <div className="iframe-login-modal-left-panel-content">
                                {displayImage()}
                                </div>
                            </div>} */}
                            <div className="iframe-login-modal-right-panel">
                                {renderCurrentStep()}
                            </div>
                        </>
                    }
                    {/* {currentStep === 'agreement' && (
                        <>
                            {renderAgreementStep()}
                        </>
                    )} */}
                </div>
            </Modal>
        </>
    );
};

export default IframeLoginModal;